/**
 * Gramática as the shared grammar components see it.
 *
 * The mirror image of `src/apps/grammatik/course.ts`: field names in, neutral
 * shapes out, and nothing else. The two adapters are the only files in the
 * frontend that know a grammar course has a language.
 */
import type { GrammarCourse, GrammarExercise, GrammarLessonView } from '../shared/grammar/types';

import { isGrammarAnswerCorrect, isOrderCorrect } from './answer';
import { BLOCK_NAMES, type Anchor, type GramaticaLesson, type GramaticaPayload } from './types';

/**
 * The anchors run the opposite way to Grammatik's, because the learner changed
 * sides: there the question was whether German behaves like her Portuguese,
 * here it is whether Portuguese behaves like her German.
 */
const ANCHOR_LABEL: Record<Anchor, string> = {
  de: 'like German',
  en: 'like English',
  both: 'like both',
  neither: 'like neither',
};

const ANCHOR_STYLE: Record<Anchor, string> = {
  de: 'border-success/30 bg-success/10 text-success',
  en: 'border-primary/30 bg-primary/10 text-primary',
  both: 'border-border bg-muted text-muted-foreground',
  neither: 'border-destructive/40 bg-destructive/10 text-destructive font-semibold',
};

export const gramaticaCourse: GrammarCourse = {
  appId: 'gramatica',
  // The learner is a native German speaker who also reads English, so both are
  // load-bearing: a bad German explanation is not softened by a good English
  // one, it is the one she reaches for first.
  aux: ['de', 'en'],
  anchorLabel: (anchor) => ANCHOR_LABEL[anchor as Anchor] ?? anchor,
  anchorStyle: (anchor) => ANCHOR_STYLE[anchor as Anchor] ?? ANCHOR_STYLE.both,
  blockName: (block) => BLOCK_NAMES[block] ?? `Bloco ${block}`,
  isAnswerCorrect: isGrammarAnswerCorrect,
  isOrderCorrect,
};

export function toExercise(payload: GramaticaPayload): GrammarExercise {
  const base = { id: payload.id, strict: payload.accent_sensitive };
  switch (payload.kind) {
    case 'fill_blank':
      return {
        ...base,
        kind: 'fill_blank',
        prompt: payload.prompt_pt,
        answer: payload.answer,
        accept: payload.accept,
        hint: payload.hint,
      };
    case 'choice':
      return {
        ...base,
        kind: 'choice',
        prompt: payload.prompt_pt,
        instruction: payload.instruction,
        options: payload.options,
        answer: payload.answer,
      };
    case 'transform':
      return {
        ...base,
        kind: 'transform',
        given: payload.given_pt,
        instruction: payload.instruction,
        answer: payload.answer,
        accept: payload.accept,
      };
    case 'order':
      return {
        ...base,
        kind: 'order',
        chunks: payload.chunks,
        answer: payload.answer,
        also_accept: payload.also_accept,
      };
  }
}

export function toLessonView(lesson: GramaticaLesson): GrammarLessonView {
  return {
    n: lesson.n,
    slug: lesson.slug,
    block: lesson.block,
    title: lesson.title.pt,
    rule: lesson.rule,
    anchor: lesson.anchor,
    parallel: lesson.parallel
      ? {
          target: lesson.parallel.pt,
          aux: {
            ...(lesson.parallel.de ? { de: lesson.parallel.de } : {}),
            ...(lesson.parallel.en ? { en: lesson.parallel.en } : {}),
          },
        }
      : null,
    relief: lesson.relief,
    trap: lesson.trap,
    table: lesson.table,
    examples: lesson.examples.map((example) => ({
      target: example.pt,
      focus: example.focus,
      aux: { de: example.de, en: example.en },
    })),
  };
}
